import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import { ArrowPathIcon, ExclamationTriangleIcon, WrenchIcon, PencilIcon, TrashIcon, LinkIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import ToolForm from './forms/ToolForm';
import { Tool } from '../types/tool';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const ToolList: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
  const [hoveredEditId, setHoveredEditId] = useState<number | null>(null);
  const [hoveredDeleteId, setHoveredDeleteId] = useState<number | null>(null);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/tools/');
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
      setError('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const handleEdit = (tool: Tool) => {
    setSelectedTool(tool);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (toolId: number) => {
    if (!window.confirm('Are you sure you want to delete this tool?')) return;

    try {
      const response = await apiClient.delete(`/api/tools/${toolId}/`);

      if (response.status >= 200 && response.status < 300) {
        setTools(tools.filter(tool => tool.id !== toolId));
      } else {
        const errorData = response.data;
        console.error('Delete failed:', errorData);
        alert('Failed to delete tool: ' + (errorData?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting tool:', error);
      alert('Failed to delete tool');
    }
  };

  const handleToolUpdated = (updatedTool: Tool) => {
    setTools(tools.map(tool => tool.id === updatedTool.id ? updatedTool : tool));
    setIsEditModalOpen(false);
    setSelectedTool(null);
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'inline-block',
      padding: `2px ${SPACING[8]}`,
      borderRadius: '9999px',
      fontSize: TYPOGRAPHY.size.xs,
      fontWeight: TYPOGRAPHY.weight.medium,
    };
    switch (status) {
      case 'active':
        return { ...baseStyle, backgroundColor: '#DCFCE7', color: '#166534' };
      case 'paused':
        return { ...baseStyle, backgroundColor: '#FEF9C3', color: '#854D0E' };
      case 'retired':
        return { ...baseStyle, backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
      case 'planned':
        return { ...baseStyle, backgroundColor: '#DBEAFE', color: '#1E40AF' };
      default:
        return { ...baseStyle, backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
    }
  };

  const getToolTypeStyle = (toolType?: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'inline-block',
      padding: `2px ${SPACING[8]}`,
      borderRadius: '9999px',
      fontSize: TYPOGRAPHY.size.xs,
      fontWeight: TYPOGRAPHY.weight.medium,
    };
    if (!toolType) return { ...baseStyle, backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
    switch (toolType) {
      case 'software':
        return { ...baseStyle, backgroundColor: '#F3E8FF', color: '#6B21A8' };
      case 'outsourcing':
        return { ...baseStyle, backgroundColor: '#E0E7FF', color: '#3730A3' };
      case 'other':
        return { ...baseStyle, backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
      default:
        return { ...baseStyle, backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: SPACING[32],
        paddingBottom: SPACING[32],
      }}>
        <ArrowPathIcon style={{ width: '24px', height: '24px', color: COLORS.accent.default, animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: SPACING[8], color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.default }}>Loading tools...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: SPACING[12],
          fontSize: TYPOGRAPHY.body.sizes.default,
          color: COLORS.status.error,
          backgroundColor: '#FEF2F2',
          borderRadius: RADIUS,
          border: `1px solid #FECACA`,
        }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8] }} />
          {error}
        </div>
      )}

      {tools.length === 0 && !error && !loading && (
        <p style={{
          textAlign: 'center',
          color: COLORS.text.secondary,
          paddingTop: SPACING[16],
          paddingBottom: SPACING[16],
          fontSize: TYPOGRAPHY.body.sizes.default,
        }}>No tools found.</p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16],
      }}>
        {tools.map((tool) => (
          <div
            key={tool.id}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[16],
              transition: 'box-shadow 150ms ease',
              boxShadow: hoveredCardId === tool.id ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
            }}
            onMouseEnter={() => setHoveredCardId(tool.id)}
            onMouseLeave={() => setHoveredCardId(null)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                <div style={{
                  padding: SPACING[8],
                  borderRadius: '9999px',
                  backgroundColor: tool.is_internal ? '#EFF6FF' : '#EFF6FF',
                }}>
                  <WrenchIcon style={{
                    width: '20px',
                    height: '20px',
                    color: tool.is_internal ? '#2563EB' : COLORS.accent.default,
                  }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.size.base,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>{tool.name}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginTop: SPACING[4] }}>
                    <span style={getStatusStyle(tool.status)}>
                      {tool.status.charAt(0).toUpperCase() + tool.status.slice(1)}
                    </span>
                    {tool.tool_type && (
                      <span style={getToolTypeStyle(tool.tool_type)}>
                        {tool.tool_type.charAt(0).toUpperCase() + tool.tool_type.slice(1)}
                      </span>
                    )}
                    {tool.is_internal && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `2px ${SPACING[8]}`,
                        borderRadius: '9999px',
                        fontSize: TYPOGRAPHY.size.xs,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        backgroundColor: '#DBEAFE',
                        color: '#1E40AF',
                      }}>
                        <CheckIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                        Internal
                      </span>
                    )}
                    {tool.pending_review && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `2px ${SPACING[8]}`,
                        borderRadius: '9999px',
                        fontSize: TYPOGRAPHY.size.xs,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        backgroundColor: '#FFEDD5',
                        color: '#9A3412',
                      }}>
                        <ClockIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                        Review
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                <button
                  onClick={() => handleEdit(tool)}
                  style={{
                    padding: SPACING[4],
                    color: hoveredEditId === tool.id ? COLORS.text.secondary : COLORS.text.tertiary,
                    borderRadius: '9999px',
                    backgroundColor: hoveredEditId === tool.id ? COLORS.bg.hover : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredEditId(tool.id)}
                  onMouseLeave={() => setHoveredEditId(null)}
                  title="Edit tool"
                >
                  <PencilIcon style={{ width: '16px', height: '16px' }} />
                </button>
                <button
                  onClick={() => handleDelete(tool.id)}
                  style={{
                    padding: SPACING[4],
                    color: hoveredDeleteId === tool.id ? COLORS.status.error : COLORS.text.tertiary,
                    borderRadius: '9999px',
                    backgroundColor: hoveredDeleteId === tool.id ? COLORS.bg.hover : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredDeleteId(tool.id)}
                  onMouseLeave={() => setHoveredDeleteId(null)}
                  title="Delete tool"
                >
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </div>

            {tool.description && (
              <p style={{
                marginTop: SPACING[8],
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}>{tool.description}</p>
            )}

            {tool.url_or_path && (
              <div style={{
                marginTop: SPACING[8],
                display: 'flex',
                alignItems: 'center',
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.secondary,
              }}>
                <LinkIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tool.url_or_path}</span>
              </div>
            )}

            {tool.related_project && (
              <p style={{
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.secondary,
                marginTop: SPACING[8],
              }}>
                Project: {tool.related_project.name}
              </p>
            )}

            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              marginTop: SPACING[8],
              textAlign: 'right',
            }}>
              Created: {formatDate(tool.created_at)}
            </p>
          </div>
        ))}
      </div>

      {selectedTool && (
        <ToolForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTool(null);
          }}
          onToolCreated={handleToolUpdated}
          initialTool={selectedTool}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default ToolList;
